import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import type {
  InviteStaffParams,
  ProfessionalRow,
  StaffProviderProps,
} from '@/types'
import type { JSX } from 'react/jsx-runtime'

import { StaffContext } from './staff-context-context'

export const StaffProvider = ({
  children,
  chamberId,
}: StaffProviderProps): JSX.Element => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const {
    data: staff = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['staff', chamberId],
    queryFn: async (): Promise<ProfessionalRow[]> => {
      if (!chamberId) throw new Error('Chamber ID required')

      const { data, error } = await supabase
        .from('professionals')
        .select('*')
        .eq('chamber_id', chamberId)

      if (error) throw error

      return data
    },
    enabled: Boolean(chamberId),
  })

  const inviteStaffMutation = useMutation({
    mutationFn: async ({ email, role = 'associate' }: InviteStaffParams) => {
      if (!chamberId || !user) throw new Error('Context missing')

      const { error } = await supabase
        .from('professionals')
        .upsert(
          {
            chamber_id: chamberId,
            invite_email: email,
            email,
            full_name: email.split('@')[0] ?? 'Invited User',
            role,
            status: 'pending',
          },
          { onConflict: 'email,chamber_id' },
        )
        .select()
        .single()

      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate and refetch staff
      void queryClient.invalidateQueries({ queryKey: ['staff', chamberId] })
    },
  })

  const inviteStaff = useCallback(
    async (params: InviteStaffParams) => {
      try {
        await inviteStaffMutation.mutateAsync(params)

        return { error: undefined }
      } catch (error) {
        console.error(
          error instanceof Error ? error.message : 'Error inviting staff',
        )

        return {
          error:
            error instanceof Error ? error.message : 'Failed to invite staff',
        }
      }
    },
    [inviteStaffMutation],
  )

  const refreshStaff = useCallback(async (): Promise<void> => {
    await refetch()
  }, [refetch])

  return (
    <StaffContext.Provider
      value={{ staff, isLoading, refreshStaff, inviteStaff }}
    >
      {children}
    </StaffContext.Provider>
  )
}

// useStaff hook moved to hooks/use-staff.ts to fix Fast Refresh error
